import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileText, Home, Plus, Tag, Wallet } from 'lucide-react';
import Layout from '@/components/Layout';
import MonthCalendarCard from '@/components/MonthCalendarCard';

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
const toMonthParam = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export default function ExpensesPage() {
  const [pgs, setPgs] = useState([]);
  const [selectedPg, setSelectedPg] = useState('');
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [formData, setFormData] = useState({
    category: 'Maintenance',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPgs();
  }, []);

  useEffect(() => {
    if (selectedPg) {
      fetchExpensesSummary();
    }
  }, [selectedPg, selectedMonth]);

  const fetchPgs = async () => {
    try {
      const response = await api.getPGs();
      setPgs(response.data);
      if (response.data.length > 0) {
        setSelectedPg(response.data[0].id);
      } else {
        setFetching(false);
      }
    } catch (error) {
      toast.error('Failed to fetch PGs');
      setFetching(false);
    }
  };

  const fetchExpensesSummary = async () => {
    try {
      setFetching(true);
      const response = await api.getExpenseMonthlySummary(selectedPg, toMonthParam(selectedMonth));
      setSummary(response.data);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch expenses');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createExpense({
        pgId: Number(selectedPg),
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Expense added successfully!');
      setFormData({
        category: 'Maintenance',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: ''
      });
      setShowForm(false);
      fetchExpensesSummary();
    } catch (error) {
      toast.error(error.message || 'Failed to add expense');
    } finally {
      setLoading(false);
    }
  };

  if (fetching && !summary && pgs.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (pgs.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] px-4">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6">
            <Home className="w-10 h-10 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Add a PG before tracking expenses</h2>
          <p className="text-slate-600 text-center mb-6 max-w-md">
            Once your property is set up, this page will show monthwise spending and category trends.
          </p>
          <Button
            onClick={() => navigate('/setup')}
            className="h-12 px-8 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-sm"
          >
            Setup Your PG
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading">Expenses</h1>
            <p className="text-slate-600 mt-1">Track monthly spending and category breakdowns</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedPg}
              onChange={(e) => setSelectedPg(Number(e.target.value))}
              className="h-11 px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-500 min-w-[220px]"
              data-testid="expense-pg-selector"
            >
              {pgs.map((pg) => (
                <option key={pg.id} value={pg.id}>{pg.name}</option>
              ))}
            </select>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="h-11 px-6 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-sm"
              data-testid="add-expense-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Expense
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[360px,1fr] gap-6">
          <MonthCalendarCard
            title="Expense Calendar"
            subtitle="Switch months to review spending"
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            accentClassName="from-orange-100 via-white to-amber-50"
          />

          <div className="space-y-6">
            {summary && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-sm font-semibold text-slate-700 mb-1">Selected Month</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.monthly_total)}</p>
                    <p className="text-sm text-slate-500 mt-1">{summary.monthly_count} transactions in {summary.selected_month_label}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-sm font-semibold text-slate-700 mb-1">Financial Year Spend</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.financial_year_total)}</p>
                    <p className="text-sm text-slate-500 mt-1">{summary.financial_year_label}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-sm font-semibold text-slate-700 mb-1">Top Category</p>
                    <p className="text-2xl font-bold text-amber-700">{summary.categories?.[0]?.category || 'None'}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {summary.categories?.[0] ? formatCurrency(summary.categories[0].amount) : 'No expenses logged'}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Tag className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 font-heading">Category Breakdown</h2>
                      <p className="text-sm text-slate-500">{summary.selected_month_label}</p>
                    </div>
                  </div>

                  {summary.categories?.length === 0 ? (
                    <p className="text-slate-500">No categories yet for this month.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {summary.categories.map((category) => (
                        <div key={category.category} className="rounded-xl bg-slate-50 p-4 border border-slate-200">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-semibold text-slate-900">{category.category}</p>
                            <p className="text-lg font-bold text-orange-600">{formatCurrency(category.amount)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-slate-900 font-heading">Add New Expense</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Category *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-orange-200"
                  data-testid="expense-category-select"
                >
                  <option>Maintenance</option>
                  <option>Electricity</option>
                  <option>Water</option>
                  <option>WiFi</option>
                  <option>Cleaning</option>
                  <option>Repairs</option>
                  <option>Groceries</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Amount (₹) *</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="h-12 rounded-lg"
                  required
                  min="1"
                  data-testid="expense-amount-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="expense-date-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Description *</Label>
                <Input
                  placeholder="What was this expense for?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="expense-description-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setShowForm(false)}
                variant="outline"
                className="flex-1 h-11 rounded-xl"
                data-testid="expense-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                disabled={loading}
                data-testid="expense-submit-button"
              >
                {loading ? 'Adding...' : 'Add Expense'}
              </Button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-heading">Expense History</h2>
              <p className="text-sm text-slate-500">{summary?.selected_month_label || 'Selected month'}</p>
            </div>
          </div>

          {fetching ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
            </div>
          ) : summary?.expenses?.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center">
              <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-700 font-semibold">No expenses recorded for this month</p>
              <p className="text-sm text-slate-500 mt-1">Pick another month from the calendar or log a new expense.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {summary.expenses.map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
                  data-testid={`expense-item-${expense.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                        {expense.category}
                      </span>
                      <span className="text-sm text-slate-500">
                        {new Date(expense.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-slate-900 font-medium">{expense.description}</p>
                  </div>
                  <p className="text-lg font-bold text-orange-600">{formatCurrency(expense.amount)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
